// Pantalla pública `/invitacion/<token>` — donde una persona entra al negocio.
//
// Los cuatro caminos que tiene que cubrir, y por qué:
//   1. El enlace ya no sirve (usado, vencido, revocado, inventado) → se dice
//      ANTES de pedirle nada. Sin esto, alguien crea una cuenta, elige una
//      contraseña y recién ahí se entera de que el vale estaba muerto.
//   2. No tiene cuenta → crea su contraseña aquí mismo.
//   3. YA tiene cuenta (el caso MÁS común: los asesores de Living House ya
//      entran a Victorix Work con la misma cuenta) → inicia sesión y vuelve
//      AQUÍ, no al inbox. De eso se encarga el `?next=`.
//   4. Tiene sesión pero con OTRO correo → se le dice, y se le ofrece cambiar
//      de cuenta. Es el que más desconcierta si no se explica.
//
// ⚠️ Lo que esta pantalla NO decide: quién entra. Eso lo decide la base —
// `aceptar_invitacion` compara el correo de la sesión con el del vale. Aquí solo
// se explica el resultado.
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Aviso, Boton } from "@victorix/chat-ai-ui";
import { sb } from "../lib/supabase.ts";
import { useSesion } from "../lib/auth.tsx";
import { AvisoCuentaUnica, Campo, CampoClave, Estado, MarcoAuth } from "./_marco.tsx";

const FUNCIONES = `${import.meta.env.VITE_SUPABASE_URL ?? ""}/functions/v1`;

type Estado_ = "comprobando" | "muerta" | "lista";

export default function Invitacion() {
  const { token = "" } = useParams();
  const navegar = useNavigate();
  const { usuarioId, correo: correoSesion, cargando } = useSesion();

  const [estado, setEstado] = useState<Estado_>("comprobando");
  const [nombre, setNombre] = useState("");
  const [clave, setClave] = useState("");
  const [ver, setVer] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [otroCorreo, setOtroCorreo] = useState(false);
  const [yaExiste, setYaExiste] = useState(false);
  const [trabajando, setTrabajando] = useState(false);

  const volverAqui = `/invitacion/${encodeURIComponent(token)}`;

  // ¿El vale sirve? Se pregunta con la clave pública a una función que devuelve
  // un booleano PELADO: nunca dice a quién se invitó ni a qué negocio (Ley 1581
  // — el invitado es empleado de una empresa cliente, no de la casa).
  useEffect(() => {
    let vivo = true;
    sb.rpc("invitacion_viva", { p_token: token })
      .then(({ data, error }) => {
        if (!vivo) return;
        setEstado(!error && data === true ? "lista" : "muerta");
      });
    return () => { vivo = false; };
  }, [token]);

  /** Con la sesión ya abierta: la base compara el correo y da la membresía. */
  async function aceptar() {
    setTrabajando(true); setError(null); setOtroCorreo(false);
    const { error } = await sb.rpc("aceptar_invitacion", { p_token: token });
    if (error) {
      const d = `${error.details ?? ""} ${error.message ?? ""}`;
      if (/VX_OTRO_CORREO/.test(d)) { setOtroCorreo(true); setTrabajando(false); return; }
      if (/VX_YA_USADA/.test(d)) { setError("Esta invitación ya se usó."); setTrabajando(false); return; }
      if (/VX_VENCIDA/.test(d)) { setEstado("muerta"); return; }
      if (/VX_REVOCADA/.test(d)) { setEstado("muerta"); return; }
      setError("No se pudo completar. Intenta de nuevo.");
      setTrabajando(false);
      return;
    }
    // Recarga completa a propósito: `ProveedorSesion` solo relee las membresías
    // cuando cambia la sesión, y aquí lo que cambió es la BASE. Sin recargar, el
    // recién llegado vería «tu cuenta no está en ningún negocio» — que es
    // exactamente lo contrario de lo que acaba de pasar.
    window.location.href = "/";
  }

  /** Sin cuenta: se crea con el correo DEL VALE (el formulario ni pregunta el
   *  correo, a propósito) y luego se inicia sesión y se acepta. */
  async function crearCuentaYEntrar(e: React.FormEvent) {
    e.preventDefault();
    setTrabajando(true); setError(null); setYaExiste(false);

    const r = await fetch(`${FUNCIONES}/chatai-aceptar-invitacion`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, nombre: nombre.trim(), clave }),
    }).catch(() => null);

    const j = await r?.json().catch(() => null);

    if (!r || !j) { setError("No se pudo conectar. Revisa tu internet."); setTrabajando(false); return; }
    if (j.ya_existe) { setYaExiste(true); setTrabajando(false); return; }
    if (!r.ok || !j.ok) {
      if (r.status === 410) setEstado("muerta");
      else setError(j.error ?? "No se pudo crear la cuenta.");
      setTrabajando(false);
      return;
    }

    const { error: errEntrar } = await sb.auth.signInWithPassword({ email: j.email, password: clave });
    if (errEntrar) {
      // La cuenta quedó creada; solo falló entrar. Mandarlo al login es mejor
      // que dejarlo mirando un error sin salida.
      navegar(`/?next=${encodeURIComponent(volverAqui)}`, { replace: true });
      return;
    }
    await aceptar();
  }

  if (estado === "comprobando" || cargando) {
    return (
      <MarcoAuth titulo="Un momento…">
        <div className="skeleton h-11 w-full rounded-lg" />
      </MarcoAuth>
    );
  }

  if (estado === "muerta") {
    return (
      <Estado
        titulo="Este enlace ya no está disponible"
        accion={<Link to="/"><Boton tono="neutro" className="w-full">Ir a la pantalla de entrada</Boton></Link>}
      >
        <p>Puede que ya lo hayas usado, que haya vencido o que lo hayan cancelado.</p>
        <p>Pídele a quien te invitó que te mande uno nuevo — toma un segundo.</p>
      </Estado>
    );
  }

  // --- Ya tiene sesión abierta ---------------------------------------------
  if (usuarioId) {
    if (otroCorreo) {
      return (
        <Estado
          titulo="Esta invitación es para otro correo"
          accion={
            <Boton
              tono="marca" className="w-full"
              onClick={async () => {
                await sb.auth.signOut();
                navegar(`/?next=${encodeURIComponent(volverAqui)}`, { replace: true });
              }}
            >
              Entrar con otra cuenta
            </Boton>
          }
        >
          <p>
            Ahora estás dentro como <b className="text-tinta">{correoSesion}</b>, y esta
            invitación se envió a una dirección distinta.
          </p>
          <p>Sal de esta cuenta y entra con el correo donde recibiste el enlace.</p>
        </Estado>
      );
    }
    return (
      <MarcoAuth
        titulo="Te invitaron a un negocio"
        bajada={`Vas a entrar como ${correoSesion}.`}
      >
        {error && <div className="mb-4"><Aviso tono="peligro">{error}</Aviso></div>}
        <Boton tono="marca" className="w-full" disabled={trabajando} onClick={aceptar}>
          {trabajando ? "Entrando…" : "Aceptar y entrar"}
        </Boton>
      </MarcoAuth>
    );
  }

  // --- Ya tenía cuenta: que entre, y que vuelva AQUÍ ------------------------
  if (yaExiste) {
    return (
      <Estado
        titulo="Ya tienes una cuenta de Victorix"
        accion={
          <Link to={`/?next=${encodeURIComponent(volverAqui)}`}>
            <Boton tono="marca" className="w-full">Entrar con mi contraseña</Boton>
          </Link>
        }
      >
        <p>
          Entra con la contraseña que ya usas — <b className="text-tinta">no tienes que crear
          otra ni cambiarla</b>. Al entrar volvemos automáticamente a esta invitación.
        </p>
      </Estado>
    );
  }

  // --- No tiene cuenta: la crea aquí ---------------------------------------
  return (
    <MarcoAuth
      titulo="Crea tu contraseña"
      bajada="Es el último paso para entrar al WhatsApp del negocio."
    >
      <form onSubmit={crearCuentaYEntrar}>
        <div className="space-y-4">
          {/* ⭐ NO se pide el correo: sale del vale, del lado del servidor. Si se
              pidiera, alguien con un enlace ajeno podría reclamar otra dirección. */}
          <Campo
            id="nombre" etiqueta="Tu nombre" type="text" autoComplete="name" required
            minLength={2} maxLength={120} value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            ayuda="Así te van a ver tus compañeros en el panel."
          />
          <CampoClave
            id="clave" etiqueta="Contraseña" autoComplete="new-password"
            valor={clave} alCambiar={setClave} ver={ver} alVer={setVer}
            ayuda="Mínimo 8 caracteres."
          />
        </div>

        <AvisoCuentaUnica />
        {error && <div className="mt-4"><Aviso tono="peligro">{error}</Aviso></div>}

        <Boton type="submit" tono="marca" className="mt-6 w-full"
               disabled={trabajando || nombre.trim().length < 2 || clave.length < 8}>
          {trabajando ? "Creando…" : "Crear cuenta y entrar"}
        </Boton>

        <p className="mt-4 text-center text-[13px] text-tinta-2">
          ¿Ya tienes cuenta?{" "}
          <Link to={`/?next=${encodeURIComponent(volverAqui)}`} className="text-marca hover:underline">
            Entra con tu contraseña
          </Link>
        </p>
      </form>
    </MarcoAuth>
  );
}
